import { Outlet, useLocation } from "react-router-dom";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { LoadingSpinner } from "@/components/loaders/loading-spinner";
import {
  SidebarProvider,
  useSidebar,
} from "@/components/layout/side-bars/sidebar";
import {
  RightSidebarProvider,
  RightSidebarWithConfig,
  useRightSidebarConfig,
} from "@/components/layout/side-bars/right-context-sidebar";
import PageHeader from "@/components/ui/PageHeader";
import { useState, useEffect, useRef } from "react";
import { useSidebarManualOverride } from "@/hooks/useSidebarManualOverride";
import UnifiedSidebar from "./side-bars/main-sidebar/UnifiedSidebar";
import { TabProvider, useTabContext } from "@/contexts/TabContext";
import { useAutoTabs } from "@/hooks/useAutoTabs";
import { MainContentArea } from "./MainContentArea";
import { SplitPanel } from "./SplitPanel";
import { useHighlightClickHandler } from "@/hooks/useHighlightClickHandler";
import { GlobalFolderProvider, useGlobalFolderContext } from "@/providers/GlobalFolderProvider";
import { FolderProvider } from "@/providers/FolderProvider";
import { FolderStructureVisibilityProvider, useFolderStructureVisibility } from "@/contexts/FolderStructureVisibilityContext";
import StructureSidebar from "./structure-menu/StructureSidebar";
import { useRTL } from "@/providers/RTLProvider";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { useZoom } from '@/providers/ZoomProvider';
import { CommandPaletteProvider, useCommandPalette } from "@/providers/CommandPaletteProvider";
import { LensesProvider } from "@/contexts/LensesContext";
import { DisplayLanguageProvider } from "@/contexts/DisplayLanguageContext";
import DevRoleIndicator from "@/components/ui/DevRoleIndicator";
import LanguageSetupPrompt from "@/components/modals/LanguageSetupPrompt";
import type { ImperativePanelHandle } from "react-resizable-panels";

interface ActionButton {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  icon?: string;
}

interface AppLayoutProps {
  borderEffect?: "black" | "none" | "gradient" | "rgb";
}

// Sidebar is now always collapsed - folder structure is controlled independently
// These route-based sidebar behaviors are no longer needed

// Component to handle right sidebar closing on route change
const SidebarRouteHandler = () => {
  const location = useLocation();
  const { close } = useRightSidebarConfig();

  useEffect(() => {
    // Close right sidebar on route change
    close();
  }, [location.pathname, close]);

  return null;
};

const AppLayout: React.FC<AppLayoutProps> = ({
  borderEffect = "gradient",
}) => {
  const { isLoading: userDataLoading } = useSimplifiedUserData();
  const [isEditMode, setIsEditMode] = useState(false);

  // Show loading spinner only for user data loading (not auth loading)
  if (userDataLoading) {
    return (
      <div className="flex flex-col min-h-screen w-full">
        <div className="flex flex-1 items-center justify-center">
          <LoadingSpinner size="lg" text="Loading your projects..." />
        </div>
      </div>
    );
  }

  return (
    <RightSidebarProvider>
      <SidebarProvider defaultOpen={false}>
        <FolderStructureVisibilityProvider>
          <TabProvider>
            <CommandPaletteProvider>
              <GlobalFolderProvider>
                <DisplayLanguageProvider>
                  <LanguageSetupPrompt />
                  <TabEnabledLayout borderEffect={borderEffect} isEditMode={isEditMode} setIsEditMode={setIsEditMode} />
                </DisplayLanguageProvider>
              </GlobalFolderProvider>
              <RightSidebarWithConfig />
              <DevRoleIndicator />
            </CommandPaletteProvider>
          </TabProvider>
        </FolderStructureVisibilityProvider>
      </SidebarProvider>
    </RightSidebarProvider>
  );
};

// Component that uses the TabContext and includes auto-tab functionality
const TabEnabledLayout: React.FC<{
  borderEffect: 'black' | 'none' | 'gradient' | 'rgb';
  isEditMode: boolean;
  setIsEditMode: (mode: boolean) => void;
}> = ({ borderEffect, isEditMode, setIsEditMode }) => {
  useAutoTabs(); // Enable automatic tab management
  useHighlightClickHandler(); // Enable click handlers for highlighted text
  const { getSelectedFolderForType } = useFolderStructureVisibility();
  const { hasSplitPanel } = useTabContext();
  const location = useLocation();
  const { isRTL } = useRTL(); // Get RTL mode for layout swapping
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const sidebarPanelRef = useRef<ImperativePanelHandle>(null);
  const { zoom } = useZoom();
  const { open: openCommandPalette } = useCommandPalette();

  // Determine current itemType from URL path
  const getCurrentItemType = (): 'library' | 'documents' | 'blueprints' | 'dialogs' | 'flows' | null => {
    const path = location.pathname;
    if (path.startsWith('/library')) return 'library';
    if (path.startsWith('/documents')) return 'documents';
    if (path.startsWith('/templates')) return 'blueprints';
    if (path.startsWith('/dialogs')) return 'dialogs';
    if (path.startsWith('/logic')) return 'flows';
    return null;
  };

  // Get the selected folder for the current page's itemType
  const currentItemType = getCurrentItemType();
  const selectedFolderId = currentItemType ? getSelectedFolderForType(currentItemType) : null;

  // Global keyboard shortcut for command palette (Ctrl-O or Cmd-O)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+O or Cmd+O (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'o') {
        e.preventDefault();
        openCommandPalette();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openCommandPalette]);

  // Determine if StructureSidebar should be visible based on the current route
  const shouldShowStructureSidebar = () => {
    const path = location.pathname;
    
    // Show on detail pages only:
    // - /library/:id (ObjectPage - Library Objects)
    // - /templates/:id (BlueprintItemPage)
    // - /documents/:id (DocumentDetailPage)
    // - /dialogs/:id (DialogItemPage)
    const detailPagePatterns = [
      /^\/library\/[^/]+$/,           // /library/:id (Object detail page)
      /^\/templates\/[^/]+$/,          // /templates/:id (Template detail page)
      /^\/documents\/[^/]+$/,          // /documents/:id (Document detail page)
      /^\/dialogs\/[^/]+$/,            // /dialogs/:id (Dialog detail page)
    ];
    
    return detailPagePatterns.some(pattern => pattern.test(path));
  };

  // Check if we're on a template page (needs LensesProvider)
  const isTemplatePage = () => {
    const path = location.pathname;
    return /^\/templates\/[^/]+$/.test(path);
  };

  const showStructureSidebar = shouldShowStructureSidebar();
  const needsLensesContext = isTemplatePage();

  // Sync sidebar panel collapse state with ref (keeps MainContentArea mounted to prevent dialog edit state loss)
  useEffect(() => {
    if (!showStructureSidebar || !sidebarPanelRef.current) return;
    if (isSidebarCollapsed) {
      sidebarPanelRef.current.collapse();
    } else {
      sidebarPanelRef.current.expand();
    }
  }, [showStructureSidebar, isSidebarCollapsed]);

  // Calculate inverse dimensions to fill viewport after zoom
  // At 80% zoom, container needs to be 125% to fill 100% after scaling
  const inverseZoom = 100 / zoom;

  return (
    <div 
      className="flex overflow-x-hidden"
      style={{ 
        zoom: zoom,
        width: `${inverseZoom}vw`,
        height: `${inverseZoom}vh`,
      }}
    >
      <SidebarRouteHandler />
      
      {/* Let the browser handle RTL naturally via document dir attribute */}
      {/* Always render in same order: UnifiedSidebar → MainContent → StructureSidebar */}
      {/* When dir="rtl" is set on <html>, browser automatically reverses flex order */}
      {/* LTR: UnifiedSidebar (left) | MainContent (center) | StructureSidebar (right) */}
      {/* RTL: UnifiedSidebar (right) | MainContent (center) | StructureSidebar (left) */}
      
      <UnifiedSidebar 
        borderEffect={borderEffect}
      />
      
      {showStructureSidebar ? (
        needsLensesContext ? (
          <LensesProvider>
            <ResizablePanelGroup direction="horizontal" className="flex-1">
              <ResizablePanel defaultSize={isSidebarCollapsed ? 100 : 80} minSize={60}>
                <div className="h-full overflow-x-hidden">
                  {hasSplitPanel ? (
                    <ResizablePanelGroup direction="horizontal" className="h-full">
                      <ResizablePanel defaultSize={50} minSize={30}>
                        <FolderProvider selectedFolderId={selectedFolderId}>
                          <MainContentArea 
                            isEditMode={isEditMode} 
                            setIsEditMode={setIsEditMode} 
                          />
                        </FolderProvider>
                      </ResizablePanel>
                      <ResizableHandle withHandle />
                      <ResizablePanel defaultSize={50} minSize={30}>
                        <SplitPanel />
                      </ResizablePanel>
                    </ResizablePanelGroup>
                  ) : (
                    <FolderProvider selectedFolderId={selectedFolderId}>
                      <MainContentArea 
                        isEditMode={isEditMode} 
                        setIsEditMode={setIsEditMode} 
                      />
                    </FolderProvider>
                  )}
                </div>
              </ResizablePanel>
              
              <ResizableHandle withHandle />
              
              <ResizablePanel 
                ref={sidebarPanelRef}
                defaultSize={isSidebarCollapsed ? 0 : 20} 
                minSize={0}
                maxSize={40}
                collapsible
                onCollapse={() => setIsSidebarCollapsed(true)}
                onExpand={() => setIsSidebarCollapsed(false)}
              >
                <StructureSidebar 
                  isCollapsed={isSidebarCollapsed}
                  onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  currentPath={location.pathname}
                />
              </ResizablePanel>
            </ResizablePanelGroup>
          </LensesProvider>
        ) : (
          <ResizablePanelGroup direction="horizontal" className="flex-1">
            <ResizablePanel defaultSize={isSidebarCollapsed ? 100 : 80} minSize={60}>
              <div className="h-full overflow-x-hidden">
                {hasSplitPanel ? (
                  <ResizablePanelGroup direction="horizontal" className="h-full">
                    <ResizablePanel defaultSize={50} minSize={30}>
                      <FolderProvider selectedFolderId={selectedFolderId}>
                        <MainContentArea 
                          isEditMode={isEditMode} 
                          setIsEditMode={setIsEditMode} 
                        />
                      </FolderProvider>
                    </ResizablePanel>
                    <ResizableHandle withHandle />
                    <ResizablePanel defaultSize={50} minSize={30}>
                      <SplitPanel />
                    </ResizablePanel>
                  </ResizablePanelGroup>
                ) : (
                  <FolderProvider selectedFolderId={selectedFolderId}>
                    <MainContentArea 
                      isEditMode={isEditMode} 
                      setIsEditMode={setIsEditMode} 
                    />
                  </FolderProvider>
                )}
              </div>
            </ResizablePanel>
            
            <ResizableHandle withHandle />
            
            <ResizablePanel 
              ref={sidebarPanelRef}
              defaultSize={isSidebarCollapsed ? 0 : 20} 
              minSize={0}
              maxSize={40}
              collapsible
              onCollapse={() => setIsSidebarCollapsed(true)}
              onExpand={() => setIsSidebarCollapsed(false)}
            >
              <StructureSidebar 
                isCollapsed={isSidebarCollapsed}
                onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                currentPath={location.pathname}
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        )
      ) : (
        <div className="flex-1 min-w-0 overflow-x-hidden">
          {hasSplitPanel ? (
            <ResizablePanelGroup direction="horizontal" className="h-full">
              <ResizablePanel defaultSize={50} minSize={30}>
                <FolderProvider selectedFolderId={selectedFolderId}>
                  <MainContentArea 
                    isEditMode={isEditMode} 
                    setIsEditMode={setIsEditMode} 
                  />
                </FolderProvider>
              </ResizablePanel>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={50} minSize={30}>
                <SplitPanel />
              </ResizablePanel>
            </ResizablePanelGroup>
          ) : (
            <FolderProvider selectedFolderId={selectedFolderId}>
              <MainContentArea 
                isEditMode={isEditMode} 
                setIsEditMode={setIsEditMode} 
              />
            </FolderProvider>
          )}
        </div>
      )}
    </div>

  );
};

export default AppLayout;